// Sprint composer-draft-attachments-persist — IndexedDB persistence layer
// for composer attachments (chips) that have already been uploaded to the
// backend. Pairs with `web/src/stores/composer-attachments.store.ts`
// (in-memory Zustand store for cross-agent-switch survival).
//
// Scope: only chips in state `'uploaded'` are persisted. The original `File`
// is stored as a `Blob` so the existing ATTACHMENT_NOT_FOUND recovery path
// (Composer.tsx — `recoverAttachmentsAndRetrySend`) can silently re-upload
// if the backend's 24h orphan TTL elapsed before the user came back.
//
// Pending / failed chips are NOT persisted (see sprint spec §3 for the
// rationale: resuming an aborted upload on reload is more complex than the
// founder benefit, and a failed chip already needs user re-action).
//
// All operations degrade gracefully:
//   - `indexedDB` undefined (private window / SSR) → silent no-op
//   - DOMException on any path → catch + log + return null/undefined
//
// Zero throws cross this boundary. The composer never has to handle IDB
// errors — it just gets `null` from a failed load (and falls back to an
// empty draft).

import type { AttachmentChipModel, ChipRoute } from './attachments';

const DB_NAME = 'asoasis-composer-attachments';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';

// Stored shape — one record per agent. Persisted via structured clone so
// the Blob travels intact.
export interface PersistedAgentDraft {
  agentId: string;
  messageId: string;
  chips: PersistedChip[];
  savedAt: number;
}

export interface PersistedChip {
  id: string;
  messageId: string;
  fileBlob: Blob;
  fileName: string;
  fileLastModified: number;
  displayName: string;
  size: number;
  mimeType: string;
  predictedRoute: ChipRoute;
  // Always 'uploaded' for persisted chips. Stored explicitly so the type
  // narrows + future schema additions can fan out from a single state field.
  state: 'uploaded';
  progress: 1;
  serverRoute: ChipRoute;
  label: string;
}

function hasIDB(): boolean {
  return typeof indexedDB !== 'undefined';
}

// Single shared open promise. Reused across calls; only re-opened if a
// prior open failed (the cached promise is reset on failure).
let openPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!hasIDB()) return Promise.resolve(null);
  if (openPromise) return openPromise;
  openPromise = new Promise<IDBDatabase | null>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'agentId' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // If another tab triggers a version change, close the connection so
      // it doesn't block the upgrade. We don't persist after that — the
      // next operation re-opens fresh.
      db.onversionchange = () => {
        db.close();
        openPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      openPromise = null;
      resolve(null);
    };
    req.onblocked = () => {
      openPromise = null;
      resolve(null);
    };
  });
  return openPromise;
}

function tx(
  db: IDBDatabase,
  mode: IDBTransactionMode,
): { store: IDBObjectStore; done: Promise<void> } {
  const transaction = db.transaction(STORE_NAME, mode);
  const store = transaction.objectStore(STORE_NAME);
  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
  return { store, done };
}

function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Load the persisted draft (uploaded chips only) for an agent. Returns
 * null if no draft exists, if IDB is unavailable, or if any error occurs.
 */
export async function loadAgentDraft(
  agentId: string,
): Promise<PersistedAgentDraft | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const { store, done } = tx(db, 'readonly');
    const got = await reqAsPromise(store.get(agentId));
    await done;
    if (!got) return null;
    return got as PersistedAgentDraft;
  } catch {
    return null;
  }
}

/**
 * Save the agent's draft. The caller is expected to pass ONLY uploaded
 * chips with a Blob — pending/failed chips are filtered upstream. We
 * defensively re-filter here so a misuse never lands an unusable record
 * (e.g. a chip whose `file` was already nulled out).
 *
 * Returns nothing — failures (quota exceeded, IDB unavailable) are
 * swallowed silently. The chip still lives in the in-memory store, so
 * agent-switch survival is unaffected; only reload survival is lost.
 */
export async function saveAgentDraft(
  agentId: string,
  draft: Omit<PersistedAgentDraft, 'savedAt'>,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const safeChips = draft.chips.filter(
    (c) => c.state === 'uploaded' && c.fileBlob instanceof Blob,
  );
  const record: PersistedAgentDraft = {
    agentId,
    messageId: draft.messageId,
    chips: safeChips,
    savedAt: Date.now(),
  };
  try {
    const { store, done } = tx(db, 'readwrite');
    store.put(record);
    await done;
  } catch {
    // Quota exceeded or transaction aborted — silent. Agent-switch
    // survival still works via the in-memory store.
  }
}

/**
 * Remove the persisted draft for an agent. Called after a successful
 * send (the chips were claimed by the backend) or when the user clears
 * the draft.
 */
export async function clearAgentDraft(agentId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const { store, done } = tx(db, 'readwrite');
    store.delete(agentId);
    await done;
  } catch {
    // Silent — if we can't clear, the next saveAgentDraft will overwrite.
  }
}

/**
 * Reconstruct an in-memory `AttachmentChipModel` (matching the type the
 * Composer's store expects) from a persisted record. The original
 * `AbortController` is not restored (uploads are not resumed); the chip
 * is already in `'uploaded'` state with a live `serverRoute`, so no
 * further upload action is needed unless the backend GC'd the file —
 * in which case the existing ATTACHMENT_NOT_FOUND recovery path
 * re-uploads from `chip.file` (which we reconstruct here from the Blob).
 */
export function hydrateChip(persisted: PersistedChip): AttachmentChipModel {
  const file = new File([persisted.fileBlob], persisted.fileName, {
    type: persisted.mimeType,
    lastModified: persisted.fileLastModified,
  });
  return {
    id: persisted.id,
    messageId: persisted.messageId,
    file,
    displayName: persisted.displayName,
    size: persisted.size,
    mimeType: persisted.mimeType,
    predictedRoute: persisted.predictedRoute,
    state: 'uploaded',
    progress: 1,
    serverRoute: persisted.serverRoute,
    label: persisted.label,
  };
}

/**
 * Convert an in-memory `AttachmentChipModel` (uploaded state, has File)
 * into a persistable record. Returns null if the chip cannot be safely
 * persisted (wrong state or missing File).
 */
export function chipToPersisted(chip: AttachmentChipModel): PersistedChip | null {
  if (chip.state !== 'uploaded') return null;
  if (!chip.serverRoute) return null;
  if (!(chip.file instanceof File)) return null;
  return {
    id: chip.id,
    messageId: chip.messageId,
    fileBlob: chip.file,
    fileName: chip.file.name,
    fileLastModified: chip.file.lastModified,
    displayName: chip.displayName,
    size: chip.size,
    mimeType: chip.mimeType,
    predictedRoute: chip.predictedRoute,
    state: 'uploaded',
    progress: 1,
    serverRoute: chip.serverRoute,
    label: chip.label,
  };
}

// Test-only: reset the cached open promise so a fresh open happens after
// `indexedDB.deleteDatabase` between tests. Not used at runtime.
export function __resetForTests(): void {
  openPromise = null;
}
